<template>
  <div class="manufacturer-list">
    <div v-if="loading && !tab" class="text-center my-10"><v-progress-circular indeterminate color="primary" /></div>

    <template v-else-if="tab">
      <div class="text-caption text--secondary mb-3">
        {{ $tc('manufacturer.list.summary', tab.manufacturers.length, {total: tab.total, manufacturers: tab.manufacturers.length, covered: covered}) }}
      </div>

      <!-- On cherche un constructeur par son nom ou une de ses marques ; ceux sans borne restent cachés par défaut -->
      <div class="d-flex align-center flex-wrap mb-3" style="gap: 16px">
        <v-btn v-if="canEditManufacturer" small color="primary" @click="creating = {name: '', aliases: '', saving: false}"><v-icon small left>mdi-plus</v-icon>{{ $t('manufacturer.list.new') }}</v-btn>
        <v-text-field v-model="search" :label="$t('manufacturer.list.search')" prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable autofocus style="max-width: 360px" />
        <v-switch v-model="withChargingPointsOnly" :label="$t('manufacturer.list.with-charging-points-only')" dense hide-details class="mt-0 pt-0" />
        <v-spacer />
        <span class="text-caption text--secondary">{{ $tc('manufacturer.list.shown', shown.length, {count: shown.length, total: tab.manufacturers.length}) }}</span>
      </div>

      <!-- Un constructeur à partir de rien : son nom et les marques que ses bornes annoncent -->
      <v-dialog :value="!!creating" max-width="480" @input="creating = null">
        <v-card v-if="creating">
          <v-card-title>{{ $t('manufacturer.list.new') }}</v-card-title>
          <v-card-text>
            <v-text-field v-model="creating.name" :label="$t('manufacturer.sheet.name')" outlined dense autofocus />
            <v-text-field v-model="creating.aliases" :label="$t('manufacturer.list.new-aliases')" :hint="$t('manufacturer.list.new-aliases-hint')" persistent-hint outlined dense />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn small text @click="creating = null">{{ $t('cancel') }}</v-btn>
            <v-btn small color="primary" :disabled="!creating.name.trim()" :loading="creating.saving" @click="createManufacturer">{{ $t('save') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <div class="text-subtitle-1 font-weight-medium mb-2">{{ $t('manufacturer.list.configured') }}</div>
      <v-row v-if="shown.length" dense class="mb-4">
        <v-col v-for="manufacturer in shown" :key="manufacturer.id" cols="12" sm="6" md="4" lg="3">
          <v-card outlined class="fill-height" :to="{name: 'manufacturer', params: {id: manufacturer.id}}">
            <v-card-text>
              <div class="d-flex align-baseline justify-space-between">
                <span class="text-subtitle-1 font-weight-medium text--primary">{{ manufacturer.name }}</span>
                <span class="text-caption">{{ $tc('manufacturer.list.charging-points', manufacturer.total, {count: manufacturer.total}) }}</span>
              </div>
              <div class="text-caption text--secondary mb-3">{{ manufacturer.aliases.join(' · ') }}</div>
              <div class="state-bar mb-3">
                <span v-for="state in STATES" :key="state" :class="adviceColor(state)" :style="{width: share(manufacturer, state)}" />
              </div>
              <div class="d-flex justify-space-between">
                <div v-for="state in STATES" :key="state">
                  <div class="text-h6" :class="manufacturer.counters[state] ? adviceColor(state) + '--text' : 'text--secondary'">{{ manufacturer.counters[state] }}</div>
                  <div class="text-caption">{{ $t('firmware.advice.' + state) }}</div>
                </div>
              </div>
              <div class="text-caption text--secondary mt-2">
                <template v-if="manufacturer.latestValidated">{{ $t('manufacturer.list.latest', {version: manufacturer.latestValidated}) }} · </template>
                {{ $tc('manufacturer.list.firmwares', manufacturer.firmwares, {count: manufacturer.firmwares}) }}
                <template v-if="manufacturer.testing"> · {{ $tc('manufacturer.list.testing', manufacturer.testing, {count: manufacturer.testing}) }}</template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-alert v-else type="info" text dense class="mb-4">{{ tab.manufacturers.length ? $t('manufacturer.list.none-found') : $t('manufacturer.list.none') }}</v-alert>

      <!-- Les marques annoncées qu'aucun constructeur ne revendique encore : seulement s'il en reste -->
      <template v-if="unassigned.length">
      <div class="d-flex align-baseline flex-wrap mb-2" style="gap: 12px">
        <div class="text-subtitle-1 font-weight-medium">{{ $t('manufacturer.list.unassigned') }}</div>
        <div class="text-caption text--secondary">{{ $t('manufacturer.list.unassigned-hint') }}</div>
      </div>
      <v-card outlined>
        <v-data-table
            :headers="headers"
            :items="unassigned"
            :items-per-page="25"
            :footer-props="{'items-per-page-options': [25, 50, -1]}"
            :no-data-text="$t('manufacturer.list.all-assigned')"
            item-key="vendor"
        >
          <template #item.actions="{item}">
            <template v-if="canEditManufacturer">
              <v-btn v-if="item.suggestion" x-small outlined color="primary" class="mr-2" :loading="acting === item.vendor" @click="addAlias(item)">
                {{ $t('manufacturer.list.add-alias', {name: item.suggestion.name}) }}
              </v-btn>
              <v-btn x-small outlined :loading="acting === item.vendor" @click="create(item)">{{ $t('manufacturer.list.create') }}</v-btn>
            </template>
          </template>
        </v-data-table>
      </v-card>
      <div class="text-caption text--secondary mt-2">{{ $t('manufacturer.list.alias-hint') }}</div>
      </template>
    </template>
  </div>
</template>

<script>
import FirmwareRepository from "@repository/FirmwareRepository";
import ManufacturerRepository from "@repository/ManufacturerRepository";
import FirmwareMixin from "@pages/Firmware/FirmwareMixin";

/**
 * L'onglet Constructeurs : on cherche un constructeur par son nom ou une de ses marques (ceux sans borne cachés par
 * défaut) ; une carte par constructeur (où en sont ses bornes, d'un coup d'œil), puis, s'il en reste, les marques que les
 * bornes annoncent et qu'aucun constructeur ne revendique : on en crée un, ou on la rattache comme alias.
 */
export default {
  mixins: [FirmwareMixin],

  data() {
    return {
      tab: null,
      loading: false,
      acting: null,
      search: '',
      creating: null,
      withChargingPointsOnly: true,
      STATES: ['up-to-date', 'update-available', 'no-path', 'not-covered', 'pinned'],
    }
  },

  computed: {
    // Par nom ou par marque annoncée ; les plus grands parcs d'abord.
    shown() {
      const words = (this.search || '').toLowerCase().split(/\s+/).filter(word => word)
      return this.tab.manufacturers
        .filter(manufacturer => !this.withChargingPointsOnly || manufacturer.total > 0 || words.length)
        .filter(manufacturer => {
          const haystack = [manufacturer.name, ...manufacturer.aliases].join(' ').toLowerCase()
          return words.every(word => haystack.includes(word))
        })
        .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
    },
    unassigned() {
      const words = (this.search || '').toLowerCase().split(/\s+/).filter(word => word)
      return this.tab.unassigned.filter(item => words.every(word => (item.vendor || '').toLowerCase().includes(word)))
    },
    covered() {
      return this.tab.manufacturers.reduce((sum, manufacturer) => sum + manufacturer.total, 0)
    },
    headers() {
      return [
        {text: this.$t('manufacturer.list.columns.vendor'), value: 'vendor'},
        {text: this.$t('manufacturer.list.columns.models'), value: 'models', align: 'end'},
        {text: this.$t('manufacturer.list.columns.versions'), value: 'versions', align: 'end'},
        {text: this.$t('manufacturer.list.columns.charging-points'), value: 'count', align: 'end'},
        {text: '', value: 'actions', sortable: false, align: 'end'},
      ]
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    load() {
      this.loading = true
      ManufacturerRepository.list().then(tab => {
        this.tab = tab
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.loading = false
      })
    },

    share(manufacturer, state) {
      return manufacturer.total ? `${(manufacturer.counters[state] / manufacturer.total) * 100}%` : '0'
    },

    createManufacturer() {
      const name = this.creating.name.trim()
      const aliases = [...new Set([name, ...this.creating.aliases.split(',').map(alias => alias.trim()).filter(alias => alias)])]
      this.creating.saving = true
      ManufacturerRepository.save({name, aliases}).then(({id}) => {
        this.$router.push({name: 'manufacturer', params: {id}})
      }).catch(err => {
        this.notifyError(err)
        this.creating.saving = false
      })
    },

    // Un constructeur à partir de la marque telle qu'elle est annoncée ; on le renomme ensuite sur sa fiche.
    create(item) {
      this.acting = item.vendor
      ManufacturerRepository.save({name: item.vendor, aliases: [item.vendor]}).then(({id}) => {
        this.$router.push({name: 'manufacturer', params: {id}})
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.acting = null
      })
    },

    addAlias(item) {
      this.acting = item.vendor
      ManufacturerRepository.get(item.suggestion.id).then(manufacturer => ManufacturerRepository.save({
        id: manufacturer.id,
        name: manufacturer.name,
        aliases: [...manufacturer.aliases, item.vendor],
        firmwareProtocol: manufacturer.firmwareProtocol,
        diagnosticProtocol: manufacturer.diagnosticProtocol,
        modelRules: manufacturer.modelRules,
      })).then(() => {
        this.notifySuccess('manufacturer.list.alias-added')
        this.load()
      }).catch(err => {
        this.notifyError(err)
      }).finally(() => {
        this.acting = null
      })
    },
  },
}
</script>

<style scoped lang="scss">
.manufacturer-list {
  .state-bar {
    display: flex;
    height: 8px;
    border-radius: 999px;
    overflow: hidden;
    background: #eaeef2;
  }
}
</style>
